import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import ReportCard from './ReportCard';

const DocumentLibrary = ({ reportType, reports }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');

  const dateFilterOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'this-year', label: 'This Year' }
  ];

  const sortOptions = [
    { value: 'date-desc', label: 'Newest First' },
    { value: 'date-asc', label: 'Oldest First' },
    { value: 'name-asc', label: 'Name A-Z' },
    { value: 'name-desc', label: 'Name Z-A' },
    { value: 'type', label: 'By Type' }
  ];

  const filterReports = () => {
    let filtered = reports || [];

    // Filter by report type
    if (reportType !== 'all') {
      filtered = filtered?.filter(report => 
        report?.type?.toLowerCase() === reportType?.toLowerCase()
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered?.filter(report =>
        report?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        report?.summary?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Filter by date
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case 'last-7-days':
          filterDate?.setDate(now?.getDate() - 7);
          break;
        case 'last-30-days':
          filterDate?.setDate(now?.getDate() - 30);
          break;
        case 'last-90-days':
          filterDate?.setDate(now?.getDate() - 90);
          break;
        case 'this-year':
          filterDate?.setMonth(0, 1);
          break;
      }
      
      filtered = filtered?.filter(report => 
        new Date(report?.generatedDate) >= filterDate
      );
    }

    // Sort reports
    switch (sortBy) {
      case 'date-desc':
        filtered?.sort((a, b) => new Date(b?.generatedDate) - new Date(a?.generatedDate));
        break;
      case 'date-asc':
        filtered?.sort((a, b) => new Date(a?.generatedDate) - new Date(b?.generatedDate));
        break;
      case 'name-asc':
        filtered?.sort((a, b) => a?.title?.localeCompare(b?.title));
        break;
      case 'name-desc':
        filtered?.sort((a, b) => b?.title?.localeCompare(a?.title));
        break;
      case 'type':
        filtered?.sort((a, b) => a?.type?.localeCompare(b?.type));
        break;
    }

    return filtered;
  };

  const filteredReports = filterReports();

  const reportTypeTitle = {
    all: 'All Documents',
    compliance: 'Compliance Reports',
    treatment: 'Treatment Reports',
    performance: 'Performance Analytics'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-lg border border-border agricultural-shadow p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{reportTypeTitle?.[reportType] || 'Document Library'}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {filteredReports?.length} {filteredReports?.length === 1 ? 'document' : 'documents'} found
            </p>
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <Input
              type="search"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full sm:w-64"
            />
            
            <Select
              options={dateFilterOptions}
              value={dateFilter}
              onChange={setDateFilter}
              className="w-full sm:w-48"
            />
            
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              className="w-full sm:w-48"
            />
          </div>
        </div>
      </div>
      {/* Document Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-success/10 rounded-lg">
              <Icon name="FileCheck" size={20} className="text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Compliance</p>
              <p className="text-xl font-semibold text-foreground">
                {reports?.filter(r => r?.type === 'Compliance')?.length || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Droplets" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Treatment</p>
              <p className="text-xl font-semibold text-foreground">
                {reports?.filter(r => r?.type === 'Treatment')?.length || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-accent/10 rounded-lg">
              <Icon name="TrendingUp" size={20} className="text-accent" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Performance</p>
              <p className="text-xl font-semibold text-foreground">
                {reports?.filter(r => r?.type === 'Performance')?.length || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Document List */}
      <div className="bg-card rounded-lg border border-border agricultural-shadow">
        <div className="p-6">
          {filteredReports?.length > 0 ? (
            <div className="space-y-4">
              {filteredReports?.map(report => (
                <ReportCard key={report?.id} report={report} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="FolderOpen" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No documents found matching your criteria</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => {
                  setSearchTerm('');
                  setDateFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentLibrary;